import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import api from '../services/authService';
import { Company } from '../types';
import { colors } from '../styles/colors';
import { formatCNPJ, maskCEP, maskPhone } from '../utils/formatters';
import CompanyForm from './CompanyForm';
import ConfirmModal from './ConfirmModal';
import CompanyOpportunities from './CompanyOpportunities';
import CompanySubscription from './CompanySubscription';
import CompanyRecruiters from './CompanyRecruiters';
import CompanySubscriptionsModal from './CompanySubscriptionsModal';
import CompanySubscriptionCreateModal from './CompanySubscriptionCreateModal';
import CompanyCredits from './CompanyCredits';
import CompanyTrialModal from './CompanyTrialModal';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface CompanyDetailProps {
  companyId?: number;
  onClose?: () => void;
}

interface CompanyAddress {
  id?: number;
  name?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  zip?: string;
  city?: string;
  state?: string;
  country?: string;
  telephone?: string;
  telephone_2?: string;
}

interface CompanyNetwork {
  id?: number;
  username?: string;
  network_name?: string;
  slug?: string;
  url?: string;
}

interface CompanyDashboard {
  published_last_90_days?: number | string | null;
  active_opportunities?: number | string | null;
  total_opportunities?: number | string | null;
  total_applies?: number | string | null;
  applies_last_90_days?: number | string | null;
  last_published_at?: string | null;
  contacts_opened_last_90_days?: number | string | null;
  recruiters_count?: number | string | null;
  subscription_status?: 'Assinante' | 'Trial' | null;
  current_subscription?: {
    id?: number;
    active?: boolean | number;
    trial?: boolean | number;
    activated_at?: string;
    deactivated_at?: string;
    canceled_at?: string;
    trial_ends_at?: string;
    mrr_value?: number | string | null;
    bill_value?: number | string | null;
    candidates_database_role?: number;
    candidates_database_contacts?: number;
    recruiters_slots?: number;
    opportunities_slots?: number;
    plan_name?: string;
  } | null;
}

interface DisplayContactsTimeline {
  months: Array<{ key: string; label: string }>;
  series: Array<{ name: string; total?: number; data: number[] }>;
  profiles: Array<{ name: string; total: number }>;
  total: number;
}

interface OpportunitiesCategoryTimeline {
  months: Array<{ key: string; label: string }>;
  series: Array<{ name: string; total?: number; data: number[] }>;
  categories: Array<{ name: string; total: number }>;
  total: number;
}

interface DetailedCompany extends Company {
  addresses?: CompanyAddress[];
  networks?: CompanyNetwork[];
  slug?: { name?: string } | null;
  billing?: Record<string, any>;
  dashboard?: CompanyDashboard;
  facebook_company_id?: string;
  trampos_career_url?: string | null;
}

const DISPLAY_CONTACTS_MONTH_OPTIONS = [3, 6, 12, 24];

const CompanyDetail: React.FC<CompanyDetailProps> = ({ companyId, onClose }) => {
  const params = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const resolvedCompanyId = companyId || Number(params.companyId);
  const [company, setCompany] = useState<DetailedCompany | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState(false);
  const [showOpportunities, setShowOpportunities] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showRecruiters, setShowRecruiters] = useState(false);
  const [showSubscriptionsModal, setShowSubscriptionsModal] = useState(false);
  const [showCreateSubscription, setShowCreateSubscription] = useState(false);
  const [showCredits, setShowCredits] = useState(false);
  const [showTrial, setShowTrial] = useState(false);
  const [displayContactsTimeline, setDisplayContactsTimeline] = useState<DisplayContactsTimeline | null>(null);
  const [displayContactsLoading, setDisplayContactsLoading] = useState(false);
  const [displayContactsMonths, setDisplayContactsMonths] = useState(12);
  const [opportunitiesCategoryTimeline, setOpportunitiesCategoryTimeline] = useState<OpportunitiesCategoryTimeline | null>(null);
  const [opportunitiesCategoryLoading, setOpportunitiesCategoryLoading] = useState(false);
  const [opportunitiesCategoryMonths, setOpportunitiesCategoryMonths] = useState(12);

  useEffect(() => {
    fetchCompany();
  }, [resolvedCompanyId]);

  useEffect(() => {
    let isMounted = true;

    const fetchDisplayContactsTimeline = async () => {
      if (!resolvedCompanyId) {
        setDisplayContactsTimeline(null);
        return;
      }

      try {
        setDisplayContactsLoading(true);
        const response = await api.get(`/companies/${resolvedCompanyId}/display-contacts-timeline`, {
          params: { months: displayContactsMonths }
        });

        if (isMounted) {
          setDisplayContactsTimeline(response.data);
        }
      } catch (error: any) {
        if (isMounted) {
          setDisplayContactsTimeline(null);
          toast.error('Erro ao carregar gráfico do Banco de Talentos');
        }
      } finally {
        if (isMounted) {
          setDisplayContactsLoading(false);
        }
      }
    };

    fetchDisplayContactsTimeline();

    return () => {
      isMounted = false;
    };
  }, [resolvedCompanyId, displayContactsMonths]);

  useEffect(() => {
    let isMounted = true;

    const fetchOpportunitiesCategoryTimeline = async () => {
      if (!resolvedCompanyId) {
        setOpportunitiesCategoryTimeline(null);
        return;
      }

      try {
        setOpportunitiesCategoryLoading(true);
        const response = await api.get(`/companies/${resolvedCompanyId}/opportunities-category-timeline`, {
          params: { months: opportunitiesCategoryMonths }
        });

        if (isMounted) {
          setOpportunitiesCategoryTimeline(response.data);
        }
      } catch (error: any) {
        if (isMounted) {
          setOpportunitiesCategoryTimeline(null);
          toast.error('Erro ao carregar gráfico de oportunidades por categoria');
        }
      } finally {
        if (isMounted) {
          setOpportunitiesCategoryLoading(false);
        }
      }
    };

    fetchOpportunitiesCategoryTimeline();

    return () => {
      isMounted = false;
    };
  }, [resolvedCompanyId, opportunitiesCategoryMonths]);

  const isPresent = (value: any) => value !== null && value !== undefined && value !== '';

  const formatDate = (dateString?: string | null, withTime = false) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    return withTime ? date.toLocaleString('pt-BR') : date.toLocaleDateString('pt-BR');
  };

  const formatCount = (value?: number | string | null) => {
    if (value === null || value === undefined || value === '') return '-';
    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? '-' : numberValue.toLocaleString('pt-BR');
  };

  const formatCurrency = (value?: number | string | null) => {
    if (value === null || value === undefined || value === '') return '-';
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) return '-';

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numberValue);
  };

  const normalizeBoolean = (value?: boolean | number | null) => value === true || value === 1;

  const renderBadge = (label: string, tone: 'success' | 'muted' | 'warning' | 'danger' | 'primary' = 'muted') => (
    <span style={{ ...styles.badge, ...badgeTone[tone] }}>{label}</span>
  );

  const renderBooleanBadge = (
    value?: boolean | number | null,
    positiveLabel = 'Sim',
    negativeLabel = 'Não'
  ) => normalizeBoolean(value)
    ? renderBadge(positiveLabel, 'primary')
    : renderBadge(negativeLabel, 'muted');

  const renderLink = (value?: string | null) => {
    if (!value) return '-';
    const href = value.startsWith('http') ? value : `https://${value}`;
    return (
      <a href={href} target="_blank" rel="noreferrer" style={styles.link}>
        {value}
      </a>
    );
  };

  const copyToClipboard = async (value: string) => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      toast.success('Link copiado');
    } catch (error) {
      toast.error('Não foi possível copiar o link');
    }
  };

  const renderCopyableLink = (value?: string | null) => {
    if (!value) return '-';

    return (
      <div style={styles.copyLinkRow}>
        <a href={value} target="_blank" rel="noreferrer" style={styles.link}>
          {value}
        </a>
        <button type="button" onClick={() => copyToClipboard(value)} style={styles.copyButton}>
          Copiar
        </button>
      </div>
    );
  };

  const renderInfoRow = (label: string, value: React.ReactNode, key?: string) => (
    <div style={styles.infoRow} key={key || label}>
      <div style={styles.infoLabel}>{label}</div>
      <div style={styles.infoValue}>{isPresent(value) ? value : '-'}</div>
    </div>
  );

  const getStatusBadge = () => {
    if (!company) return null;
    return company.status === 1
      ? renderBadge('ATIVO', 'success')
      : renderBadge('INATIVO', 'danger');
  };

  const getSubscriptionStatusBadge = () => {
    const status = company?.dashboard?.subscription_status;
    if (status === 'Assinante') return renderBadge('Assinante', 'primary');
    if (status === 'Trial') return renderBadge('Trial', 'warning');
    return renderBadge('Nenhum status', 'muted');
  };

  const getAddressLine = (address: CompanyAddress) => {
    const parts = [
      address.street,
      address.number ? `nº ${address.number}` : null,
      address.complement
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : '-';
  };

  const getLocationLine = (address: CompanyAddress) => {
    const cityState = [address.city, address.state].filter(Boolean).join(' - ');
    return [address.neighborhood, cityState, address.country].filter(Boolean).join(' · ') || '-';
  };

  const fetchCompany = async () => {
    if (!resolvedCompanyId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/companies/${resolvedCompanyId}`);
      setCompany(response.data);
    } catch (error: any) {
      toast.error('Erro ao carregar detalhes da empresa');
    } finally {
      setLoading(false);
    }
  };

  const goBackToCompanies = () => {
    if (onClose) {
      onClose();
      return;
    }

    const companyListId = company?.id || resolvedCompanyId;
    navigate(companyListId ? `/companies?companyId=${companyListId}` : '/companies');
  };

  const handleEditSave = async () => {
    setShowEditForm(false);
    await fetchCompany();
  };

  const closeActionMenu = () => setOpenActionMenu(false);

  const handleEdit = () => {
    closeActionMenu();
    setShowEditForm(true);
  };

  const handleToggleStatus = () => {
    closeActionMenu();
    setShowConfirmModal(true);
  };

  const confirmToggleStatus = async () => {
    if (!company) return;

    try {
      const newStatus = company.status === 1 ? 0 : 1;
      await api.put(`/companies/${company.id}`, { status: newStatus });
      toast.success(`Empresa ${newStatus === 1 ? 'ativada' : 'inativada'} com sucesso!`);
      setShowConfirmModal(false);
      await fetchCompany();
    } catch (error) {
      toast.error('Erro ao alterar status da empresa');
    }
  };

  const handleOpenOpportunities = () => {
    closeActionMenu();
    setShowOpportunities(true);
  };

  const handleOpenSubscriptions = () => {
    closeActionMenu();
    setShowSubscriptionsModal(true);
  };

  const handleOpenCurrentPlan = () => {
    closeActionMenu();
    setShowSubscription(true);
  };

  const handleOpenRecruiters = () => {
    closeActionMenu();
    setShowRecruiters(true);
  };

  const handleOpenCredits = () => {
    closeActionMenu();
    setShowCredits(true);
  };

  const handleCreateSubscription = () => {
    setShowSubscriptionsModal(false);
    setShowCreateSubscription(true);
  };

  const handleCreateTrial = () => {
    setShowSubscriptionsModal(false);
    setShowTrial(true);
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loading}>Carregando detalhes da empresa...</div>
      </div>
    );
  }

  if (!company) {
    return (
      <div style={styles.page}>
        <button type="button" onClick={goBackToCompanies} style={styles.backButton}>
          {onClose ? '← Fechar detalhes' : '← Voltar para Empresas'}
        </button>
        <div style={styles.emptyState}>Empresa não encontrada.</div>
      </div>
    );
  }

  const dashboard = company.dashboard || {};
  const subscription = dashboard.current_subscription;
  const displayContactsSeries = displayContactsTimeline?.series || [];
  const displayContactsProfiles = displayContactsTimeline?.profiles || [];
  const opportunitiesCategorySeries = opportunitiesCategoryTimeline?.series || [];
  const opportunitiesCategoryCategories = opportunitiesCategoryTimeline?.categories || [];
  const tramposCareerUrl = company.trampos_career_url || null;
  const bowieCompanyUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/companies/${company.id}/details`;
  const chartPalette = ['#26366F', '#3FA34D', '#E09F3E', '#2F80ED', '#9B51E0', '#EB5757', '#00A8A8', '#6B7280'];

  const buildLineChartData = (
    labels: string[],
    series: Array<{ name: string; data: number[] }>,
    totalLabel = 'Total'
  ) => ({
    labels,
    datasets: series.map((serie, index) => {
      const isTotal = serie.name === 'Total';
      const color = isTotal ? '#151515' : chartPalette[index % chartPalette.length];

      return {
        label: serie.name === 'Total' ? totalLabel : serie.name,
        data: serie.data,
        borderColor: color,
        backgroundColor: color,
        borderWidth: isTotal ? 3 : 2,
        borderDash: isTotal ? [8, 5] : undefined,
        pointRadius: isTotal ? 4 : 3,
        pointHoverRadius: 6,
        tension: 0.32,
        fill: false
      };
    })
  });

  const displayContactsChartData = buildLineChartData(
    displayContactsTimeline?.months.map(month => month.label) || [],
    displayContactsSeries
  );

  const opportunitiesCategoryChartData = buildLineChartData(
    opportunitiesCategoryTimeline?.months.map(month => month.label) || [],
    opportunitiesCategorySeries
  );

  const buildLineChartOptions = (yTitle: string) => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          color: '#151515',
          boxWidth: 14,
          boxHeight: 14,
          padding: 16,
          font: {
            size: 13,
            weight: 600
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${formatCount(context.parsed.y)}`
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#4E5560',
          font: {
            size: 13,
            weight: 600
          }
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          color: '#4E5560',
          font: {
            size: 13,
            weight: 600
          }
        },
        title: {
          display: true,
          text: yTitle,
          color: '#4E5560',
          font: {
            size: 13,
            weight: 700
          }
        },
        grid: {
          color: '#ECEEF2'
        }
      }
    }
  });

  return (
    <div style={styles.page} onClick={closeActionMenu}>
      <div style={styles.pageHeader}>
        <button type="button" onClick={goBackToCompanies} style={styles.backButton}>
          {onClose ? '← Fechar detalhes' : '← Voltar para Empresas'}
        </button>
        <div style={styles.headerCard}>
          <div>
            <h1 style={styles.title}>{company.name}</h1>
            <div style={styles.headerMeta}>
              <span style={styles.companyId}>ID: {company.id}</span>
              {getStatusBadge()}
              {getSubscriptionStatusBadge()}
            </div>
          </div>
          <div style={styles.headerActions} onClick={event => event.stopPropagation()}>
            <div style={styles.actionMenuContainer}>
              <button
                type="button"
                onClick={() => setOpenActionMenu(current => !current)}
                style={styles.actionMenuButton}
              >
                Ações ▾
              </button>
              {openActionMenu && (
                <div style={styles.actionMenu}>
                  <button type="button" onClick={handleOpenOpportunities} style={styles.actionMenuItem}>
                    Oportunidades
                  </button>
                  <button type="button" onClick={handleOpenSubscriptions} style={styles.actionMenuItem}>
                    Assinaturas
                  </button>
                  <button type="button" onClick={handleOpenCurrentPlan} style={styles.actionMenuItem}>
                    Plano Atual
                  </button>
                  <button type="button" onClick={handleOpenRecruiters} style={styles.actionMenuItem}>
                    Recrutadores
                  </button>
                  <button type="button" onClick={handleOpenCredits} style={styles.actionMenuItem}>
                    Créditos
                  </button>
                  <button type="button" onClick={handleEdit} style={styles.actionMenuItem}>
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={handleToggleStatus}
                    style={{
                      ...styles.actionMenuItem,
                      color: company.status === 1 ? colors.error : colors.success
                    }}
                  >
                    {company.status === 1 ? 'Inativar' : 'Ativar'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <section style={styles.section}>
        <div style={styles.dashboardGrid}>
          <div style={styles.metricCard}>
            <span style={styles.metricLabel}>Vagas 90 dias</span>
            <strong style={styles.metricValue}>{formatCount(dashboard.published_last_90_days)}</strong>
          </div>
          <div style={styles.metricCard}>
            <span style={styles.metricLabel}>Vagas ativas</span>
            <strong style={styles.metricValue}>{formatCount(dashboard.active_opportunities)}</strong>
          </div>
          <div style={styles.metricCard}>
            <span style={styles.metricLabel}>Última publicação</span>
            <strong style={styles.metricValueSmall}>{formatDate(dashboard.last_published_at)}</strong>
          </div>
          <div style={styles.metricCard}>
            <span style={styles.metricLabel}>Contatos abertos 90 dias</span>
            <strong style={styles.metricValue}>{formatCount(dashboard.contacts_opened_last_90_days)}</strong>
          </div>
          <div style={styles.metricCard}>
            <span style={styles.metricLabel}>Status comercial</span>
            <strong style={styles.metricValueSmall}>{dashboard.subscription_status || '-'}</strong>
          </div>
          <div style={styles.metricCard}>
            <span style={styles.metricLabel}>Recrutadores</span>
            <strong style={styles.metricValue}>{formatCount(dashboard.recruiters_count)}</strong>
          </div>
          <div style={styles.metricCard}>
            <span style={styles.metricLabel}>MRR</span>
            <strong style={styles.metricValueSmall}>{formatCurrency(subscription?.mrr_value)}</strong>
          </div>
          <div style={styles.metricCard}>
            <span style={styles.metricLabel}>Candidaturas 90 dias</span>
            <strong style={styles.metricValue}>{formatCount(dashboard.applies_last_90_days)}</strong>
          </div>
        </div>
      </section>

      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>🏢 Informações Básicas</h3>
        <div style={styles.infoTable}>
          {renderInfoRow('Página de carreira Trampos', renderCopyableLink(tramposCareerUrl))}
          {renderInfoRow('Link compartilhável Bowie', renderCopyableLink(bowieCompanyUrl))}
          {renderInfoRow('Razão Social', company.name)}
          {renderInfoRow('Nome fantasia', company.fancy_name)}
          {renderInfoRow('Slug', company.slug?.name)}
          {renderInfoRow('CNPJ', formatCNPJ(company.cnpj))}
          {renderInfoRow('Email', company.email ? <a href={`mailto:${company.email}`} style={styles.link}>{company.email}</a> : '-')}
          {renderInfoRow('Segmento da empresa', company.segment)}
          {renderInfoRow('Website', renderLink(company.url))}
          {renderInfoRow('Funcionários', company.employees)}
          {renderInfoRow('Descrição', company.description ? <div style={styles.descriptionBox}>{company.description}</div> : '-')}
        </div>
      </section>

      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>🏷️ Características</h3>
        <div style={styles.infoTable}>
          {renderInfoRow('Startup', renderBooleanBadge(company.startup))}
          {renderInfoRow('Ponto de contato', renderBooleanBadge(company.pto_contato))}
          {renderInfoRow('IA Assistant', renderBooleanBadge(company.ia_opportunity_assistant, 'Ativo', 'Inativo'))}
          {renderInfoRow('Cliente', renderBooleanBadge(company.customer))}
          {renderInfoRow('Empresa estrangeira', renderBooleanBadge(company.foreign_company))}
          {renderInfoRow('Diversidade e inclusão', renderBooleanBadge(company.is_eoe, 'Sim', 'Não'))}
        </div>
      </section>

      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>📍 Endereços</h3>
        {company.addresses && company.addresses.length > 0 ? (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Tipo</th>
                  <th style={styles.th}>Endereço</th>
                  <th style={styles.th}>Bairro/Cidade</th>
                  <th style={styles.th}>CEP</th>
                  <th style={styles.th}>Telefone</th>
                </tr>
              </thead>
              <tbody>
                {company.addresses.map((address, index) => (
                  <tr key={address.id || index}>
                    <td style={styles.td}>{renderBadge(address.name || 'Empresa', 'muted')}</td>
                    <td style={styles.td}>{getAddressLine(address)}</td>
                    <td style={styles.td}>{getLocationLine(address)}</td>
                    <td style={styles.td}>{address.zip ? maskCEP(address.zip) : '-'}</td>
                    <td style={styles.td}>
                      {[address.telephone, address.telephone_2].filter(Boolean).map(phone => maskPhone(phone as string)).join(' / ') || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={styles.emptyState}>Nenhum endereço cadastrado</div>
        )}
      </section>

      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>🔗 Redes Sociais</h3>
        <div style={styles.infoTable}>
          {company.networks && company.networks.length > 0
            ? company.networks.map((network, index) => renderInfoRow(network.network_name || network.slug || `Rede ${index + 1}`, network.username || '-', `network-${index}`))
            : renderInfoRow('Redes cadastradas', '-')}
          {renderInfoRow('Facebook', company.facebook_company_id)}
          {renderInfoRow('LinkedIn ID', company.linkedin_id)}
          {renderInfoRow('Compartilhar vagas no LinkedIn', normalizeBoolean(company.linkedin_share_jobs) ? renderBadge('PERMITIDO', 'success') : renderBadge('BLOQUEADO', 'danger'))}
        </div>
      </section>

      <section style={styles.section}>
        <div style={styles.chartPanel}>
          <div style={styles.chartHeader}>
            <div>
              <h3 style={styles.sectionTitle}>📈 Banco de Talentos</h3>
              <p style={styles.chartSubtitle}>
                Aberturas de contato por perfil nos últimos {displayContactsTimeline?.months.length || displayContactsMonths} meses
              </p>
            </div>
            <div style={styles.chartHeaderActions}>
              {displayContactsLoading && displayContactsTimeline && (
                <span style={styles.chartLoadingBadge}>Atualizando...</span>
              )}
              <label style={styles.periodField}>
                <span style={styles.periodLabel}>Período</span>
                <select
                  value={displayContactsMonths}
                  onChange={event => setDisplayContactsMonths(Number(event.target.value))}
                  style={styles.periodSelect}
                >
                  {DISPLAY_CONTACTS_MONTH_OPTIONS.map(months => (
                    <option value={months} key={months}>
                      {months} meses
                    </option>
                  ))}
                </select>
              </label>
              <div style={styles.chartSummary}>
                <span style={styles.chartSummaryLabel}>Total no período</span>
                <strong style={styles.chartSummaryValue}>{formatCount(displayContactsTimeline?.total)}</strong>
              </div>
            </div>
          </div>

          {displayContactsLoading && !displayContactsTimeline ? (
            <div style={styles.chartLocalLoading}>Carregando gráfico do Banco de Talentos...</div>
          ) : displayContactsSeries.length > 0 ? (
            <>
              <div style={styles.chartCanvas}>
                <Line
                  data={displayContactsChartData}
                  options={buildLineChartOptions('Contatos abertos')}
                />
              </div>

              <div style={styles.profileList}>
                {displayContactsProfiles.slice(0, 8).map(profile => (
                  <div style={styles.profilePill} key={profile.name}>
                    <span style={styles.profileName}>{profile.name}</span>
                    <strong style={styles.profileTotal}>{formatCount(profile.total)}</strong>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={styles.emptyState}>
              Nenhuma abertura de contato encontrada nos últimos {displayContactsTimeline?.months.length || displayContactsMonths} meses
            </div>
          )}
        </div>
      </section>

      <section style={styles.section}>
        <div style={styles.chartPanel}>
          <div style={styles.chartHeader}>
            <div>
              <h3 style={styles.sectionTitle}>📊 Oportunidades por categoria</h3>
              <p style={styles.chartSubtitle}>
                Vagas publicadas por categoria nos últimos {opportunitiesCategoryTimeline?.months.length || opportunitiesCategoryMonths} meses
              </p>
            </div>
            <div style={styles.chartHeaderActions}>
              {opportunitiesCategoryLoading && opportunitiesCategoryTimeline && (
                <span style={styles.chartLoadingBadge}>Atualizando...</span>
              )}
              <label style={styles.periodField}>
                <span style={styles.periodLabel}>Período</span>
                <select
                  value={opportunitiesCategoryMonths}
                  onChange={event => setOpportunitiesCategoryMonths(Number(event.target.value))}
                  style={styles.periodSelect}
                >
                  {DISPLAY_CONTACTS_MONTH_OPTIONS.map(months => (
                    <option value={months} key={months}>
                      {months} meses
                    </option>
                  ))}
                </select>
              </label>
              <div style={styles.chartSummary}>
                <span style={styles.chartSummaryLabel}>Total no período</span>
                <strong style={styles.chartSummaryValue}>{formatCount(opportunitiesCategoryTimeline?.total)}</strong>
              </div>
            </div>
          </div>

          {opportunitiesCategoryLoading && !opportunitiesCategoryTimeline ? (
            <div style={styles.chartLocalLoading}>Carregando gráfico de oportunidades...</div>
          ) : opportunitiesCategorySeries.length > 0 ? (
            <>
              <div style={styles.chartCanvas}>
                <Line
                  data={opportunitiesCategoryChartData}
                  options={buildLineChartOptions('Oportunidades publicadas')}
                />
              </div>

              <div style={styles.profileList}>
                {opportunitiesCategoryCategories.slice(0, 8).map(category => (
                  <div style={styles.profilePill} key={category.name}>
                    <span style={styles.profileName}>{category.name}</span>
                    <strong style={styles.profileTotal}>{formatCount(category.total)}</strong>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={styles.emptyState}>
              Nenhuma oportunidade publicada encontrada nos últimos {opportunitiesCategoryTimeline?.months.length || opportunitiesCategoryMonths} meses
            </div>
          )}
        </div>
      </section>

      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>📅 Datas do Sistema</h3>
        <div style={styles.infoTable}>
          {renderInfoRow('Criado em', formatDate(company.created_at, true))}
          {renderInfoRow('Atualizado em', formatDate(company.updated_at, true))}
          {renderInfoRow('Ativado em', formatDate(company.activated_at, true))}
          {renderInfoRow('Inativado em', formatDate(company.inactivated_at, true))}
          {renderInfoRow('Expira em', formatDate(company.expires_at, true))}
          {renderInfoRow('Assinatura ativada em', formatDate(subscription?.activated_at, true))}
          {renderInfoRow('Trial termina em', formatDate(subscription?.trial_ends_at, true))}
          {renderInfoRow('Assinatura desativada em', formatDate(subscription?.deactivated_at, true))}
          {renderInfoRow('Assinatura cancelada em', formatDate(subscription?.canceled_at, true))}
        </div>
      </section>

      {showEditForm && (
        <CompanyForm
          company={{
            ...company,
            slug: company.slug?.name ? { name: company.slug.name } : undefined
          }}
          onSave={handleEditSave}
          onCancel={() => setShowEditForm(false)}
        />
      )}

      {showOpportunities && (
        <CompanyOpportunities
          companyId={company.id}
          companyName={company.name}
          onClose={() => setShowOpportunities(false)}
        />
      )}

      {showSubscription && (
        <CompanySubscription
          companyId={company.id}
          companyName={company.name}
          onClose={() => setShowSubscription(false)}
        />
      )}

      {showRecruiters && (
        <CompanyRecruiters
          companyId={company.id}
          companyName={company.name}
          onClose={() => setShowRecruiters(false)}
        />
      )}

      {showSubscriptionsModal && (
        <CompanySubscriptionsModal
          companyId={company.id}
          companyName={company.name}
          onClose={() => setShowSubscriptionsModal(false)}
          onCreateNew={handleCreateSubscription}
          onCreateTrial={handleCreateTrial}
        />
      )}

      {showCreateSubscription && (
        <CompanySubscriptionCreateModal
          companyId={company.id}
          companyName={company.name}
          onClose={() => setShowCreateSubscription(false)}
          onSave={async () => {
            setShowCreateSubscription(false);
            await fetchCompany();
          }}
        />
      )}

      {showCredits && (
        <CompanyCredits
          companyId={company.id}
          companyName={company.name}
          onClose={() => setShowCredits(false)}
        />
      )}

      {showTrial && (
        <CompanyTrialModal
          companyId={company.id}
          companyName={company.name}
          onClose={() => setShowTrial(false)}
          onSave={async () => {
            setShowTrial(false);
            await fetchCompany();
          }}
        />
      )}

      <ConfirmModal
        isOpen={showConfirmModal}
        title={company.status === 1 ? 'Confirmar Inativação' : 'Confirmar Ativação'}
        message={`Tem certeza que deseja ${company.status === 1 ? 'inativar' : 'ativar'} a empresa ${company.name}?`}
        onConfirm={confirmToggleStatus}
        onCancel={() => setShowConfirmModal(false)}
        confirmText={company.status === 1 ? 'Inativar' : 'Ativar'}
        cancelText="Cancelar"
      />
    </div>
  );
};

const badgeTone: { [key: string]: React.CSSProperties } = {
  success: { backgroundColor: '#3FA34D', color: colors.white },
  muted: { backgroundColor: '#E8E8EC', color: '#5F6368' },
  warning: { backgroundColor: '#F2B84B', color: '#2F2A1D' },
  danger: { backgroundColor: colors.error, color: colors.white },
  primary: { backgroundColor: colors.primary, color: colors.white }
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '22px 24px 52px',
    color: colors.text
  },
  pageHeader: {
    marginBottom: '18px'
  },
  headerCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '28px 30px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0
  },
  actionMenuContainer: {
    position: 'relative'
  },
  actionMenuButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '46px',
    padding: '0 16px',
    borderRadius: '8px',
    border: `1px solid ${colors.primary}`,
    backgroundColor: colors.primary,
    color: colors.white,
    fontSize: '0.98rem',
    fontWeight: 700,
    cursor: 'pointer'
  },
  actionMenu: {
    position: 'absolute',
    top: 'calc(100% + 10px)',
    right: 0,
    minWidth: '200px',
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12)',
    overflow: 'hidden',
    zIndex: 20
  },
  actionMenuItem: {
    width: '100%',
    padding: '12px 14px',
    border: 'none',
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.white,
    color: colors.text,
    fontSize: '0.98rem',
    fontWeight: 600,
    textAlign: 'left',
    cursor: 'pointer'
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    border: `1px solid ${colors.primary}`,
    backgroundColor: colors.white,
    color: colors.primary,
    borderRadius: '6px',
    padding: '11px 16px',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer',
    marginBottom: '16px'
  },
  title: {
    margin: '0 0 12px 0',
    color: '#151515',
    fontSize: '2rem',
    lineHeight: 1.2,
    fontWeight: 800
  },
  headerMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: colors.text,
    fontSize: '1.04rem',
    flexWrap: 'wrap'
  },
  companyId: {
    color: colors.primary,
    fontWeight: 700
  },
  section: {
    marginBottom: '32px'
  },
  sectionTitle: {
    margin: '0 0 18px 0',
    color: '#151515',
    fontSize: '1.36rem',
    fontWeight: 800
  },
  chartPanel: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
  },
  chartHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '18px',
    marginBottom: '18px',
    flexWrap: 'wrap'
  },
  chartHeaderActions: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '12px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end'
  },
  chartLoadingBadge: {
    alignSelf: 'flex-end',
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '46px',
    padding: '0 12px',
    borderRadius: '8px',
    backgroundColor: '#F7F8FA',
    border: `1px solid ${colors.borderLight}`,
    color: '#4E5560',
    fontSize: '0.9rem',
    fontWeight: 700
  },
  chartSubtitle: {
    margin: '-8px 0 0 0',
    color: '#4E5560',
    fontSize: '1rem',
    lineHeight: 1.45,
    fontWeight: 600
  },
  periodField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    minWidth: '150px'
  },
  periodLabel: {
    color: '#4E5560',
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    fontWeight: 800
  },
  periodSelect: {
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    backgroundColor: colors.white,
    color: '#151515',
    minHeight: '46px',
    padding: '0 12px',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer'
  },
  chartSummary: {
    border: `1px solid ${colors.borderLight}`,
    borderRadius: '8px',
    padding: '12px 16px',
    minWidth: '170px',
    textAlign: 'right',
    backgroundColor: '#F7F8FA'
  },
  chartSummaryLabel: {
    display: 'block',
    color: '#4E5560',
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    fontWeight: 800,
    marginBottom: '5px'
  },
  chartSummaryValue: {
    color: colors.primary,
    fontSize: '1.45rem',
    lineHeight: 1.1
  },
  chartCanvas: {
    height: '390px',
    minHeight: '390px',
    marginTop: '8px'
  },
  chartLocalLoading: {
    minHeight: '260px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#4E5560',
    fontSize: '1.05rem',
    fontWeight: 700,
    backgroundColor: '#FBFBFC',
    border: `1px solid ${colors.borderLight}`,
    borderRadius: '8px'
  },
  profileList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '10px',
    marginTop: '18px'
  },
  profilePill: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    border: `1px solid ${colors.borderLight}`,
    borderRadius: '8px',
    padding: '12px 14px',
    backgroundColor: '#FBFBFC'
  },
  profileName: {
    color: '#151515',
    fontSize: '0.98rem',
    fontWeight: 700,
    overflowWrap: 'anywhere'
  },
  profileTotal: {
    color: colors.primary,
    fontSize: '1.05rem',
    whiteSpace: 'nowrap'
  },
  dashboardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px'
  },
  metricCard: {
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '22px 18px',
    minHeight: '122px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    backgroundColor: colors.white,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
  },
  metricLabel: {
    color: '#4E5560',
    textTransform: 'uppercase',
    fontSize: '0.82rem',
    lineHeight: 1.25,
    marginBottom: '12px',
    fontWeight: 800
  },
  metricValue: {
    color: colors.primary,
    fontSize: '2.15rem',
    lineHeight: 1.1
  },
  metricValueSmall: {
    color: colors.primary,
    fontSize: '1.32rem',
    lineHeight: 1.2
  },
  infoTable: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
  },
  infoRow: {
    display: 'grid',
    gridTemplateColumns: '300px minmax(0, 1fr)',
    borderBottom: `1px solid ${colors.borderLight}`,
    minHeight: '58px'
  },
  infoLabel: {
    padding: '18px 22px',
    color: '#4E5560',
    fontSize: '1rem',
    fontWeight: 700,
    backgroundColor: '#FBFBFC'
  },
  infoValue: {
    padding: '18px 22px',
    color: '#151515',
    fontSize: '1.05rem',
    lineHeight: 1.45,
    minWidth: 0,
    overflowWrap: 'anywhere'
  },
  descriptionBox: {
    backgroundColor: '#F7F8FA',
    borderRadius: '4px',
    padding: '14px',
    whiteSpace: 'pre-wrap'
  },
  tableWrapper: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    overflowX: 'auto',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '920px'
  },
  th: {
    padding: '17px 18px',
    color: '#4E5560',
    fontSize: '0.82rem',
    textTransform: 'uppercase',
    textAlign: 'left',
    borderBottom: `1px solid ${colors.borderLight}`,
    backgroundColor: '#F7F8FA',
    fontWeight: 800
  },
  td: {
    padding: '17px 18px',
    color: '#151515',
    fontSize: '1rem',
    lineHeight: 1.42,
    borderBottom: `1px solid ${colors.borderLight}`,
    verticalAlign: 'middle'
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '24px',
    padding: '3px 10px',
    borderRadius: '999px',
    fontSize: '0.78rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    lineHeight: 1
  },
  link: {
    color: colors.primary,
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    fontWeight: 600
  },
  copyLinkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap'
  },
  copyButton: {
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.white,
    color: colors.primary,
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '0.9rem',
    fontWeight: 700,
    cursor: 'pointer'
  },
  emptyState: {
    padding: '22px',
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    fontSize: '1.05rem',
    color: '#4E5560',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)'
  },
  loading: {
    textAlign: 'center',
    padding: '2.4rem',
    fontSize: '1.35rem',
    color: colors.textLight
  }
};

export default CompanyDetail;
